import { GoogleLogo, FacebookLogo, Star, ArrowUpRight, Quotes } from "@phosphor-icons/react";
import Reveal from "@/components/site/Reveal";
import { FACEBOOK_URL, GOOGLE_REVIEW_URL, REVIEWS } from "@/lib/siteData";

function Stars({ count = 5 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${count} star rating`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} size={18} weight="fill" className={i < count ? "text-gold" : "text-white/15"} />
      ))}
    </div>
  );
}

function ReviewCard({ review, delay }) {
  return (
    <Reveal delay={delay}>
      <figure className="h-full border border-line bg-surface p-7 flex flex-col" data-testid="review-card">
        <Quotes size={28} weight="fill" className="text-gold/40" />
        <blockquote className="mt-4 text-white/80 leading-relaxed flex-1">{review.text}</blockquote>
        <figcaption className="mt-6 flex items-center justify-between gap-3">
          <div>
            <p className="font-heading font-semibold text-white">{review.name}</p>
            {review.location && <p className="text-white/40 text-sm">{review.location}</p>}
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <Stars count={review.rating || 5} />
            {review.source === "Facebook" ? (
              <FacebookLogo size={16} weight="fill" className="text-white/35" />
            ) : (
              <GoogleLogo size={16} weight="bold" className="text-white/35" />
            )}
          </div>
        </figcaption>
      </figure>
    </Reveal>
  );
}

export default function Reviews() {
  const hasReviews = Array.isArray(REVIEWS) && REVIEWS.length > 0;

  return (
    <section id="reviews" data-testid="reviews-section" className="relative py-24 lg:py-32 bg-ink overflow-hidden">
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[500px] h-[500px] bg-gold/10 blur-3xl rounded-full pointer-events-none" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-5">Reviews</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading font-bold tracking-tight text-white text-4xl sm:text-5xl leading-[1.05]">
              Hear it from our <span className="text-white/50">neighbors.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-6 flex items-center gap-3">
              <Stars count={5} />
              <span className="text-white/70 text-sm">Trusted by homeowners across Central Texas</span>
            </div>
          </Reveal>
        </div>

        {hasReviews ? (
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reviews-grid">
            {REVIEWS.map((r, i) => (
              <ReviewCard key={`${r.name}-${i}`} review={r} delay={0.05 * i} />
            ))}
          </div>
        ) : (
          <Reveal delay={0.1}>
            <div className="mt-14 border border-line bg-surface p-9 lg:p-12 text-center max-w-3xl mx-auto" data-testid="reviews-empty">
              <div className="mx-auto flex h-14 w-14 items-center justify-center bg-gold/10 text-gold">
                <GoogleLogo size={30} weight="bold" />
              </div>
              <h3 className="mt-7 font-heading font-semibold text-white text-2xl tracking-tight">
                Be the first to leave a review
              </h3>
              <p className="mt-3 text-white/60 leading-relaxed max-w-lg mx-auto">
                We just launched online. If we&apos;ve worked on your home, we&apos;d be grateful if you shared your
                experience — it helps other Central Texas families find honest, local craftsmanship.
              </p>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-google-button"
              className="group inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-6 py-3.5 hover:bg-gold-hover transition-colors duration-200"
            >
              <GoogleLogo size={18} weight="bold" />
              Leave us a Google review
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-facebook-button"
              className="group inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-6 py-3.5 hover:border-gold hover:text-gold transition-colors duration-200"
            >
              <FacebookLogo size={18} weight="fill" />
              Find us on Facebook
              <ArrowUpRight size={18} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
